import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import UPCOMING_EVENTS from "../data/upcoming-events.json";
import PAST_EVENTS from "../data/past-events.json";

const Navbar = () => {
	const { pathname } = useLocation();
	const [scrolled, setScrolled] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const dropdownRef = useRef(null);

	const navItems = [
		{ name: "Home", path: "/" },
		{ name: "About", path: "/about" },
		{ name: "Programs", path: "/programs" },
		{ name: "Council Members", path: "/council-members" },
		{ name: "Get Membership", path: "/membership" },
		{ name: "Events & News", path: "/events" },
		{ name: "Contact", path: "/contact" },
	];

	// Scroll to top + close everything on route change
	useEffect(() => {
		window.scrollTo(0, 0);
		setMenuOpen(false);
		setDropdownOpen(false);
	}, [pathname]);

	// Shadow on scroll
	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 50);
		window.addEventListener("scroll", onScroll);
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	// Close dropdown when clicking outside
	useEffect(() => {
		const onClickOutside = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", onClickOutside);
		return () => document.removeEventListener("mousedown", onClickOutside);
	}, []);

	const applyLink = "https://www.eventbrite.ca/e/leadership-and-peace-summit-morocco-2027-tickets-2001166056545?aff=oddtdtcreator";
	const isExternal = applyLink.startsWith("http");

	const activeCls = "text-[#1666ba] font-semibold";
	const inactiveCls = "text-gray-700 hover:text-[#1666ba]";
	const baseCls = "px-2 py-2 rounded-md text-sm font-medium transition-colors duration-200";

	return (
		<nav className={`fixed w-full z-50 bg-white transition-all duration-300 ${scrolled ? "shadow-md py-2" : "shadow-lg py-3"}`}>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex items-center justify-between">

					{/* Logo */}
					<Link to="/" className="flex-shrink-0">
						<img src="/logo-mobile.png" alt="SLD Logo" className="h-9 w-auto md:hidden" />
						<img src="/logo.png" alt="SLD Logo" className="h-9 w-auto hidden md:block" />
					</Link>

					{/* Desktop nav — hidden on mobile */}
					<div className="hidden lg:flex items-center gap-1 xl:gap-3">
						{navItems.map((item) => {
							if (item.name === "Events & News") {
								return (
									<div key={item.name} className="relative" ref={dropdownRef}>
										<button
											onClick={() => setDropdownOpen((o) => !o)}
											className={`${baseCls} flex items-center gap-1 ${
												pathname === item.path || pathname.startsWith("/event/") ? activeCls : inactiveCls
											}`}>
											{item.name}
											<svg className={`h-4 w-4 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
												fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
											</svg>
										</button>
										{dropdownOpen && (
											<motion.div
												initial={{ opacity: 0, y: 8 }}
												animate={{ opacity: 1, y: 0 }}
												className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 max-h-96 overflow-y-auto">
												<Link to="/events"
													className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#1666ba] border-b border-gray-100"
													onClick={() => setDropdownOpen(false)}>
													<div className="font-medium">All Events & News</div>
													<div className="text-xs text-gray-500 mt-1">View all upcoming events and latest news</div>
												</Link>
												{PAST_EVENTS.map((event) => (
													<Link key={event.id} to={`/event/${event.slug}`}
														className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#1666ba]"
														onClick={() => setDropdownOpen(false)}>
														<div className="font-medium">{event.title}</div>
														<div className="text-xs text-gray-500 mt-1">{event.date}</div>
													</Link>
												))}
											</motion.div>
										)}
									</div>
								);
							}
							return (
								<Link key={item.name} to={item.path}
									className={`${baseCls} ${pathname === item.path ? activeCls : inactiveCls}`}>
									{item.name}
								</Link>
							);
						})}
						{isExternal ? (
							<a href={applyLink} target="_blank" rel="noopener noreferrer"
								className="ml-2 bg-[#1666ba] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#819f3d] transition-colors duration-200">
								Apply Now
							</a>
						) : (
							<Link to={applyLink}
								className="ml-2 bg-[#1666ba] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#819f3d] transition-colors duration-200">
								Apply Now
							</Link>
						)}
					</div>

					{/* Hamburger — mobile only */}
					<button
						className="lg:hidden p-2 text-[#1666ba]"
						aria-label="Toggle menu"
						onClick={() => setMenuOpen((o) => !o)}>
						{menuOpen ? (
							<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						) : (
							<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
							</svg>
						)}
					</button>
				</div>
			</div>

			{/* Mobile menu */}
			{menuOpen && (
				<div className="lg:hidden bg-white border-t border-gray-100 shadow-lg max-h-[80vh] overflow-y-auto">
					<div className="px-3 py-3 space-y-1">
						{navItems.map((item) => {
							if (item.name === "Events & News") {
								return (
									<div key={item.name}>
										<button
											onClick={() => setDropdownOpen((o) => !o)}
											className={`w-full flex items-center justify-between px-3 py-3 rounded-md text-base font-medium ${
												pathname === item.path || pathname.startsWith("/event/")
													? "text-[#1666ba] font-semibold bg-blue-50"
													: "text-gray-700 hover:text-[#1666ba] hover:bg-gray-50"
											}`}>
											<span>{item.name}</span>
											<svg className={`h-5 w-5 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
												fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
											</svg>
										</button>
										{dropdownOpen && (
											<div className="ml-4 pl-3 border-l-2 border-[#1666ba] space-y-1 mt-1">
												<Link to="/events"
													className="block px-3 py-3 text-sm font-medium text-gray-700 hover:text-[#1666ba] hover:bg-gray-50 rounded-md">
													All Events & News
												</Link>
												<div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
													Past Events
												</div>
												{PAST_EVENTS.map((event) => (
													<Link key={event.id} to={`/event/${event.slug}`}
														className="block px-3 py-3 text-sm text-gray-700 hover:text-[#1666ba] hover:bg-gray-50 rounded-md">
														<div className="font-medium leading-snug">{event.title}</div>
														<div className="text-xs text-gray-400 mt-0.5">{event.date}</div>
													</Link>
												))}
											</div>
										)}
									</div>
								);
							}
							return (
								<Link key={item.name} to={item.path}
									className={`block px-3 py-3 rounded-md text-base font-medium ${
										pathname === item.path
											? "text-[#1666ba] font-semibold bg-blue-50"
											: "text-gray-700 hover:text-[#1666ba] hover:bg-gray-50"
									}`}>
									{item.name}
								</Link>
							);
						})}
						{isExternal ? (
							<a href={applyLink} target="_blank" rel="noopener noreferrer"
								className="block w-full text-center mt-2 bg-[#1666ba] text-white px-4 py-3 rounded-full text-base font-medium hover:bg-[#819f3d] transition-colors">
								Apply Now
							</a>
						) : (
							<Link to={applyLink}
								className="block w-full text-center mt-2 bg-[#1666ba] text-white px-4 py-3 rounded-full text-base font-medium hover:bg-[#819f3d] transition-colors">
								Apply Now
							</Link>
						)}
					</div>
				</div>
			)}
		</nav>
	);
};

export default Navbar;
